/**
 * POSLayout — kiosk-mode wrapper для POS-сторінок (без sidebar/header
 * глобального AppLayout). Тільки фіксований pos-header з workstation,
 * касиром, статусом зміни + hamburger menu.
 */
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getMe } from '@/api/auth';
import { useAuth, getRefreshToken } from '@/auth/store';
import { logout as apiLogout } from '@/api/auth';
import type { PosWorkstation } from '@/api/retailPosWorkstations';
import type { CashShiftOpen } from '@/api/retailCashShiftOpen';

interface Props {
  workstation: PosWorkstation | null;
  shift: CashShiftOpen | null;
  onOpenShift?: () => void;
  onCloseShift: () => void;
  onChangeWorkstation: () => void;
  children: ReactNode;
}

export default function POSLayout({
  workstation,
  shift,
  onOpenShift,
  onCloseShift,
  onChangeWorkstation,
  children,
}: Props) {
  const navigate = useNavigate();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: getMe });
  const [menuOpen, setMenuOpen] = useState(false);
  const clearAuth = useAuth((s) => s.clear);

  const logout = async () => {
    const refresh = getRefreshToken();
    if (refresh) {
      try {
        await apiLogout(refresh);
      } catch {
        // ignore
      }
    }
    clearAuth();
    navigate('/login', { replace: true });
  };

  return (
    <div className="pos-app">
      <header className="pos-header">
        <div className="pos-header__title">
          POS{workstation && ` · ${workstation.name}`}
        </div>
        <div className="pos-header__info">
          {meQuery.data && <>👤 {meQuery.data.full_name} · </>}
          {shift ? (
            <span className="pos-header__shift-active">
              Зміна {shift.document_number}
            </span>
          ) : (
            <span className="pos-header__shift-inactive">⚠ Зміна не відкрита</span>
          )}
        </div>
        <button
          className="pos-header__menu"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Меню"
        >
          ☰
        </button>
      </header>

      {menuOpen && (
        <div
          className="pos-menu-dropdown"
          onMouseLeave={() => setMenuOpen(false)}
        >
          {shift ? (
            <button
              onClick={() => {
                setMenuOpen(false);
                onCloseShift();
              }}
            >
              🔒 Закрити зміну
            </button>
          ) : (
            onOpenShift && (
              <button
                onClick={() => {
                  setMenuOpen(false);
                  onOpenShift();
                }}
              >
                ▶ Відкрити зміну
              </button>
            )
          )}
          <button
            onClick={() => {
              setMenuOpen(false);
              onChangeWorkstation();
            }}
          >
            🖥 Змінити робоче місце
          </button>
          <hr />
          <button
            onClick={() => {
              setMenuOpen(false);
              navigate('/');
            }}
          >
            ↩ Вийти у бух-режим
          </button>
          <button
            onClick={() => {
              setMenuOpen(false);
              logout();
            }}
          >
            ⏻ Вийти з системи
          </button>
        </div>
      )}

      <main className="pos-main">{children}</main>
    </div>
  );
}
